import {
  CFormSwitch,
  CNav,
  CNavItem,
  CNavLink,
  CSpinner,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useReducer, useState } from "react";
import BoxWrapper from "../../components/ui/BoxWrapper";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { usePaginatedContent } from "../../hooks/usePaginatedContent";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import { useSuggestedMessagesQuery } from "../../hooks/SuggestedMessages/useSuggestedMessagesQuery";
import RenderTable from "../../components/ui/RenderTable";
import SuggestionsMessageTable, {
  SuggestedMessageModal,
} from "./SuggestionsMessageTable";

const PAGE_SIZE = 10;

export default function VenezSettings() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [visible, setVisible] = useState(false);
  const [isForDriver, setIsForDriver] = useState(false);
  const [isForUser, setIsForUser] = useState(false);
  const {
    suggestedMessages,
    deleteSuggestedMessage,
    mutating,
    fetchingSuggestedMessages,
  } = useSuggestedMessagesQuery();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const paginatedContent = usePaginatedContent(
    currentPage,
    PAGE_SIZE,
    suggestedMessages,
  );

  const filteredSuggestedMessages = useMemo(() => {
    const suggestedMessages = paginatedContent?.filter(
      (suggestedMessage: any) => {
        const matchesSearchFilter =
          !searchQuery ||
          suggestedMessage?.suggestion?.toLowerCase()?.includes(searchQuery) ||
          suggestedMessage?.arabicTranslation
            ?.toLowerCase()
            ?.includes(searchQuery) ||
          suggestedMessage?.englishTranslation
            ?.toLowerCase()
            ?.includes(searchQuery);

        const matchesDriverFilter =
          !isForDriver || suggestedMessage?.isForDriver === true;
        const matchesUserFilter =
          !isForUser || suggestedMessage?.isForUser === true;

        return matchesSearchFilter && matchesDriverFilter && matchesUserFilter;
      },
    );

    return suggestedMessages;
  }, [paginatedContent, searchQuery, isForDriver, isForUser]);

  const [suggestedMessagesToBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  const handleBulkDelete = () => {
    suggestedMessagesToBeDeleted.ids.forEach((id) => {
      if (!id) return;
      deleteSuggestedMessage(id);
      suggestedMessagesToBeDeleted.ids = [];
    });
  };
  const SettingsSVG = () => {
    return (
      <svg
        width="30"
        height="30"
        viewBox="0 0 600 600"
        xmlns="http://www.w3.org/2000/svg"
        fill="white"
      >
        <path d="M259.1 73.5C262.1 58.7 275.2 48 290.4 48L350.2 48C365.4 48 378.5 58.7 381.5 73.5L396 143.5C410.1 149.5 423.3 157.2 435.3 166.3L503.1 143.8C517.5 139 533.3 145 540.9 158.2L570.8 210C578.4 223.2 575.7 239.8 564.3 249.9L511 297.3C511.9 304.7 512.3 312.3 512.3 320C512.3 327.7 511.8 335.3 511 342.7L564.4 390.2C575.8 400.3 578.4 417 570.9 430.1L541 481.9C533.4 495 517.6 501.1 503.2 496.3L435.4 473.8C423.3 482.9 410.1 490.5 396.1 496.6L381.7 566.5C378.6 581.4 365.5 592 350.4 592L290.6 592C275.4 592 262.3 581.3 259.3 566.5L244.9 496.6C230.8 490.6 217.7 482.9 205.6 473.8L137.5 496.3C123.1 501.1 107.3 495.1 99.7 481.9L69.8 430.1C62.2 416.9 64.9 400.3 76.3 390.2L129.7 342.7C128.8 335.3 128.4 327.7 128.4 320C128.4 312.3 128.9 304.7 129.7 297.3L76.3 249.8C64.9 239.7 62.3 223 69.8 209.9L99.7 158.1C107.3 144.9 123.1 138.9 137.5 143.7L205.3 166.2C217.4 157.1 230.6 149.5 244.6 143.4L259.1 73.5zM320.3 400C364.5 399.8 400.2 363.9 400 319.7C399.8 275.5 363.9 239.8 319.7 240C275.5 240.2 239.8 276.1 240 320.3C240.2 364.5 276.1 400.2 320.3 400z" />
      </svg>
    );
  };

  const totalPages = Math.ceil(suggestedMessages?.length / PAGE_SIZE);
  return (
    <AdminRoute>
      <h5 className="fs-4 fw-bold mb-2">Paramètres</h5>
      <BoxWrapper>
        <CNav variant="tabs" className="mb-2">
          <CNavItem
            href="#settings"
            active={activeTab === 0}
            onClick={() => setActiveTab(0)}
          >
            <CNavLink>Messages suggérés</CNavLink>
          </CNavItem>
        </CNav>

        <CTabContent>
          <CTabPane visible={activeTab === 0}>
            <div
              className="d-flex  justify-content-between align-items-baseline before-table"
              id="settings"
            >
              <div className="d-flex align-items-center gap-2 p-3 delete-add">
                <button
                  onClick={() => setVisible(!visible)}
                  className="outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center shadow-secondary"
                >
                  + Ajouter un message suggéré
                </button>

                <SuggestedMessageModal
                  open={visible}
                  onClose={() => setVisible(false)}
                  id=""
                  suggestions={{
                    suggestion: {
                      fr: "",
                      en: "",
                      ar: "",
                    },
                    isForDriver: false,
                    isForUser: false,
                  }}
                  mode="create"
                />

                <button
                  disabled={
                    mutating || suggestedMessagesToBeDeleted?.ids.length === 0
                  }
                  onClick={handleBulkDelete}
                  className="outline-none  btn btn-outline-danger rounded-pill text-danger d-flex align-items-center gap-2 justify-content-center shadow-primary"
                >
                  {!mutating ? (
                    <>
                      <DeleteIcon fill={colors.primary} /> Supprimer
                    </>
                  ) : (
                    <>
                      <CSpinner size="sm" /> {general.fr.wait}
                    </>
                  )}
                </button>
              </div>
              <div className="search-form">
                <div className=" input-field">
                  <MagnifyingGlassIcon color="gray" width={20} />
                  <input
                    type="text"
                    className="w-50 border-0  outline-none rounded-sm p-1 text-gray fs-6"
                    placeholder="Rechercher"
                    onChange={(e) =>
                      setSearchQuery(e?.target?.value?.toLowerCase())
                    }
                  />
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="fs-6">Pour le chauffeur</span>
                <CFormSwitch
                  checked={isForDriver}
                  onChange={() => setIsForDriver(!isForDriver)}
                  color={colors.primary}
                />
                <span className="fs-6">Pour l'utilisateur</span>
                <CFormSwitch
                  checked={isForUser}
                  onChange={() => setIsForUser(!isForUser)}
                  color={colors.primary}
                />
              </div>
              <div
                className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3"
                style={{ borderTopRightRadius: "8px", alignSelf: "flex-start" }}
              >
                <SettingsSVG />
                <p className="fs-5 fw-bold m-0  text-white text-pre">
                  {" "}
                  {suggestedMessages?.length}{" "}
                  {suggestedMessages?.length > 1
                    ? "Messages suggérés"
                    : "Message suggéré"}
                </p>
              </div>
            </div>

            <RenderTable
              loading={fetchingSuggestedMessages}
              render={() =>
                filteredSuggestedMessages?.length > 0 ? (
                  <SuggestionsMessageTable
                    suggestionsMessage={
                      filteredSuggestedMessages as SuggestedMessage[]
                    }
                    suggestionsMessageToBeDeleted={dispatch}
                  />
                ) : (
                  <p className="text-center fs-5 my-2">{general.fr.noData}</p>
                )
              }
            />
          </CTabPane>
          <CTabPane visible={activeTab === 1}></CTabPane>
        </CTabContent>
      </BoxWrapper>
      {activeTab === 0 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={(page) => {
            setCurrentPage(page);
          }}
          siblingCount={0}
          totalCount={totalPages}
          pageSize={PAGE_SIZE}
        />
      )}
    </AdminRoute>
  );
}
